/**
 * Immediate Auth State Detector
 * Provides instant feedback about auth state to prevent login screen flash
 */

import { supabase } from '../lib/supabase'

export class ImmediateAuthCheck {
  private static cachedResult: boolean | null = null
  private static lastCheck = 0
  private static readonly CACHE_DURATION = 30 * 1000 // 30 seconds

  /**
   * Performs an immediate synchronous check for likely authentication state
   * Uses cached results and localStorage clues to avoid async delays
   */
  static isLikelyAuthenticated(): boolean {
    // Return cached result if recent
    if (this.cachedResult !== null && Date.now() - this.lastCheck < this.CACHE_DURATION) {
      return this.cachedResult
    }

    try {
      // Check for auth cache
      const authCache = localStorage.getItem('clockit-auth-cache')
      if (authCache) {
        const cached = JSON.parse(authCache)
        if (cached.isAuthenticated && Date.now() < cached.expiresAt) {
          this.cachedResult = true
          this.lastCheck = Date.now()
          return true
        }
      }

      // Check for Supabase session in localStorage
      // Use a more generic approach to find supabase session keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.includes('supabase.auth.token') || (key && key.includes('-auth-token'))) {
          try {
            const sessionData = localStorage.getItem(key)
            if (sessionData) {
              const session = JSON.parse(sessionData)
              if (session.access_token && session.expires_at && session.expires_at > Date.now() / 1000) {
                this.cachedResult = true
                this.lastCheck = Date.now()
                return true
              }
            }
          } catch {
            // Invalid session data, continue checking other keys
            continue
          }
        }
      }

      // REMOVED: Grace period bypass - this was a security flaw
      // Grace period should be SESSION metadata, not a bypass mechanism
      // All dashboard access requires valid Supabase sessions

    } catch (error) {
      console.warn('ImmediateAuthCheck failed:', error)
    }

    this.cachedResult = false
    this.lastCheck = Date.now()
    return false
  }

  /**
   * Clear cached results to force fresh check
   */
  static clearCache(): void {
    this.cachedResult = null
    this.lastCheck = 0
  }
}